import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  digitalOperationsTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, DigitalEntitlementDetail } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-digital-entitlement-detail',
  imports: [
    AinPageComponent, AinStateComponent, TableModule, ButtonModule, RouterLink, DatePipe,
    DialogModule, FormsModule, TextareaModule
  ],
  template: `
    <ain-page [title]="t('catalog.digital.licenseCenter.title')" [description]="t('catalog.digital.licenseCenter.description')">
      <p class="back"><a routerLink="/digital/entitlements">{{ t('catalog.digital.entitlements.title') }}</a></p>
      <span class="live-status" [class]="liveUpdateService.connectionState()">
        {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
      </span>
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!detail()) {
        <ain-state type="empty" [title]="t('catalog.digital.licenseCenter.notFound')" />
      } @else {
        <section class="panel">
          <div class="summary">
            <span>{{ t('catalog.tracking.status') }}: <strong>{{ detail()!.status }}</strong></span>
            <span>{{ t('catalog.digital.entitlements.customer') }}: {{ detail()!.customerReference || '—' }}</span>
            <span>{{ t('catalog.digital.entitlements.credits') }}: {{ detail()!.remainingUsageCredits }} / {{ detail()!.usageCreditsGranted }}</span>
            @if (detail()!.accessEndsAtUtc) {
              <span>{{ t('catalog.digital.licenseCenter.accessEnds') }}: {{ detail()!.accessEndsAtUtc | date:'medium' }}</span>
            }
          </div>
          <div class="toolbar">
            @if (detail()!.status === 'Active') {
              <button pButton type="button" [label]="t('catalog.digital.licenseCenter.generate')" [disabled]="acting()" (click)="generateLicense()"></button>
              <button pButton type="button" severity="danger" [label]="t('catalog.digital.licenseCenter.revoke')" [disabled]="acting()" (click)="openRevokeDialog()"></button>
            }
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" [disabled]="acting()" (click)="load()"></button>
          </div>
          @if (message()) { <p class="message">{{ message() }}</p> }
          @if (generatedKey()) {
            <p class="license-key">{{ t('catalog.digital.licenseCenter.newKey') }}: <code>{{ generatedKey() }}</code></p>
          }
          <h3>{{ t('catalog.digital.licenseCenter.licenses') }}</h3>
          <p-table [value]="detail()!.licenses">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.digital.licenseCenter.prefix') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.digital.licenseCenter.created') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.keyPrefix }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.createdAtUtc | date:'medium' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }

      <p-dialog
        [header]="t('catalog.digital.licenseCenter.revokeConfirmTitle')"
        [(visible)]="revokeVisible"
        [modal]="true"
        [style]="{ width: '420px' }">
        <p class="confirm-text">{{ t('catalog.digital.licenseCenter.revokeConfirmMessage') }}</p>
        <label>{{ t('catalog.digital.licenseCenter.revokeReason') }}</label>
        <textarea pTextarea [(ngModel)]="revokeReason" rows="2" class="full"></textarea>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="revokeVisible = false"></button>
          <button pButton type="button" severity="danger" [label]="t('catalog.digital.licenseCenter.revoke')" [disabled]="acting()" (click)="confirmRevoke()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .back { margin: 0 0 8px; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
    .panel { display: grid; gap: 12px; }
    .summary { display: flex; flex-wrap: wrap; gap: 16px; font-size: 0.9rem; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .message { margin: 0; color: #166534; font-weight: 600; }
    .license-key { margin: 0; padding: 10px; background: #fef3c7; border-radius: 6px; }
    .license-key code { font-weight: 700; word-break: break-all; }
    .confirm-text { margin: 0 0 8px; }
    .full { width: 100%; }
    h3 { margin: 0; font-size: 1rem; }
  `]
})
export class DigitalEntitlementDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly detail = signal<DigitalEntitlementDetail | null>(null);
  readonly message = signal('');
  readonly generatedKey = signal('');

  revokeVisible = false;
  revokeReason = '';

  private liveEventsSubscription?: Subscription;
  private subscribedTopic = '';
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.digitalOperationsChanged))
      .subscribe(() => this.load(false));

    this.load();
    void this.subscribeToTopic();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  load(showLoading = true): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) { this.loading.set(false); return; }
    if (showLoading) this.loading.set(true);
    this.message.set('');
    this.service.getDigitalEntitlementDetail(id).subscribe({
      next: d => { this.detail.set(d); this.loading.set(false); },
      error: () => { this.detail.set(null); this.loading.set(false); }
    });
  }

  generateLicense(): void {
    const id = this.detail()?.id;
    if (!id) return;
    this.acting.set(true);
    this.generatedKey.set('');
    this.service.generateDigitalLicense(id).subscribe({
      next: license => {
        this.generatedKey.set(license.licenseKey);
        this.message.set(this.t('catalog.digital.licenseCenter.generated'));
        this.load(false);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openRevokeDialog(): void {
    this.revokeReason = '';
    this.revokeVisible = true;
  }

  confirmRevoke(): void {
    const id = this.detail()?.id;
    if (!id) return;
    this.acting.set(true);
    const reason = this.revokeReason.trim() || 'admin_revoke';
    this.service.revokeDigitalEntitlement(id, reason).subscribe({
      next: () => {
        this.revokeVisible = false;
        this.generatedKey.set('');
        this.message.set(this.t('catalog.digital.licenseCenter.revoked'));
        this.load(false);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  private async subscribeToTopic(): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId) return;
    this.subscribedTopic = digitalOperationsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }

  t(key: string): string { return this.localization.translate(key); }
}
